import React, { FunctionComponent, ReactElement, useState } from 'react';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import Image from '@empiriecom/module-components/Image';
import Price from '@empiriecom/module-components/Price';
import { Headline } from '@empiriecom/module-components/Headline';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import useDebounce from '@empiriecom/module-components/hooks/useDebounce';
import { FragmentConfiguration } from '@/config';
import { ModalSelectLayer } from '@/src/components/ProductSelection/ModalSelect';
import { useProduct, useBasket } from '@/src/components/ProductContext';
import { TaxHint } from '@/src/components/TaxHint';
import AddToBasket from '@/src/components/AddToBasket';
import AddToWishlist from '@/src/components/AddToWishlist';

const BuyBoxWrapper = styled.div<{ mobile: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  ${({ mobile }) => mobile && 'order: 1;'}
  ${({ mobile }) => mobile && 'border-bottom: 1px solid #d5d3d1;'}
  ${({ mobile }) => mobile && 'padding: 0 0.75rem 0.75rem;'}
`;

const BuyBoxImage = styled(Image)<{ mobile: boolean }>`
  width: ${({ mobile }) => (mobile ? '4.5rem' : '12.25rem')};
  min-width: ${({ mobile }) => (mobile ? '4.5rem' : '12.25rem')};
  max-width: ${({ mobile }) => (mobile ? '4.5rem' : '12.25rem')};
  height: ${({ mobile }) => (mobile ? '4.5rem' : '12.25rem')};
  min-height: ${({ mobile }) => (mobile ? '4.5rem' : '12.25rem')};
  max-height: ${({ mobile }) => (mobile ? '4.5rem' : '12.25rem')};
  margin-right: 0.75rem;
`;

const MobileHeadline = styled.div`
  > h1 {
    font-size: 1rem;
    line-height: normal;
    font-weight: normal;
  }
`;

const ButtonsWrapper = styled.div`
  margin-top: 2.25rem;
`;

export const RatingsListBuyBox: FunctionComponent = (): ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { error, isLoading, product } = useProduct();
  const isDebounceLoading = useDebounce(isLoading, 300);
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const {
    price: { layout },
  } = useConfig<FragmentConfiguration>();
  const { setRequestedSKU, setAddToBasketRequested } = useBasket();

  return (
    <>
      {error && null}
      {isDebounceLoading && null}
      {product && (
        <BuyBoxWrapper data-testid="ratings-list-buybox" mobile={isMobile}>
          <BuyBoxImage
            src={product.images[0].concat('?$format_dv_mob_075$')}
            alt={product.name}
            fallback={() => null}
            mobile={isMobile}
          />
          <div>
            {isMobile && (
              <MobileHeadline data-testid="ratings-list-buybox-mobile-headline">
                <Headline level={1}>{product.name}</Headline>
              </MobileHeadline>
            )}
            <div>
              <Price
                amount={product.price.amount && product.price.amount}
                currency={product.price.currency}
                isMSRP={product.price.uvp && product.price.uvp}
                isStartPrice={product.price.isStartPrice && product.price.isStartPrice}
                layout={layout}
                oldPrice={product.price.valueOld && product.price.valueOld}
                price={product.price.value}
                saving={product.price.saving && product.price.saving.value}
                savingPrefix
                savingType={
                  product.price.saving && product.price.saving.type
                    ? product.price.saving.type
                    : 'CURRENCY'
                }
                unit={product.price.unit && product.price.unit}
                unitPrice={product.price.valueUnit && product.price.valueUnit}
              />
              <TaxHint />
            </div>
            {!isMobile && (
              <ButtonsWrapper data-testid="ratings-list-buybox-buttons">
                <AddToBasket
                  setHighlightUnselectedDimension={() => setIsModalOpen(true)}
                  isSelected={product.availability && product.availability.state !== 'UNSELECTED'}
                />
                <div style={{ marginTop: '0.75rem' }}>
                  <AddToWishlist sku={product.sku} />
                </div>
              </ButtonsWrapper>
            )}
          </div>
        </BuyBoxWrapper>
      )}
      {isModalOpen && (
        <ModalSelectLayer
          isModalOpen={isModalOpen}
          dimension={[...product.dimensions].pop()}
          setProduct={(prodId: string) => {
            setRequestedSKU(prodId);
            setAddToBasketRequested(true);
            setIsModalOpen(false);
          }}
          testOnClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default RatingsListBuyBox;
